<template>
  <div class="known-business-entity-model-edit-form">
    <div class="primary-lighten-1 w-full space-y-2 rounded-md p-2">
      <!-- Switch between KVK search and manual entry -->
      <div v-if="isCreating" class="flex items-center justify-between">
        <span class="text-sm font-medium">Zoek in handelsregister</span>
        <InputSwitch
          v-model="useKVKSearch"
          @change="onUseKVKSearchChange"
          title="Wissel tussen KVK zoeken en handmatig invoeren"
        >
          <span class="sr-only">Wissel tussen KVK zoeken en handmatig invoeren</span>
        </InputSwitch>
      </div>

      <!-- KVK Search Mode -->
      <div v-show="useKVKSearch" class="space-y-2">
        <label v-if="isCreating" class="block">
          <span class="sr-only block text-sm">KVK-nummer</span>
          <InputCombobox
            v-model="selectedKVKItem"
            :items="KVKSearchResults"
            items-key-name="vestigingsnummer"
            :items-value-function="(item: any) => item.naam"
            :text-on-mounted="kvkTextOnMounted"
            input-placeholder="Bedrijfsnaam in het handelsregister"
            @query-changed="onKVKQueryChanged"
            @item-selected="onKVKItemSelected"
            :items-filter-fn="(item) => item.adres"
            :max-item-count="100"
            :sort-alphabetically="false"
            :editable="false"
            ref="comboboxKVK"
            grow
          >
            <template #top-of-options>
              <div class="gray-lighten-2 rounded-md p-2">Resultaten uit het handelsregister</div>
            </template>
            <template #custom-list-item="{ item, active }">
              <div
                class="py-2 pl-2"
                :class="{
                  'primary cursor-pointer': active
                }"
              >
                <div>{{ item.naam }}</div>
                <div class="text-sm" :class="active ? 'text-gray-lighten-2/50' : ''">
                  {{ item.kvkNummer }} - {{ item.adres?.binnenlandsAdres?.straatnaam }},
                  {{ item.adres?.binnenlandsAdres?.plaats }}
                </div>
              </div>
            </template>
          </InputCombobox>
        </label>

        <PAlert
          v-if="inputHasBeenGiven && KVKSearchResults.length === 0 && !isSearchingKVK"
          :type="AlertTypes.WARNING"
        >
          Geen resultaten gevonden in het handelsregister.
          <template #actions="{ buttonColor }">
            <GeneralButton :color="buttonColor" @click="switchToManualEntry">
              Handmatig toevoegen
            </GeneralButton>
          </template>
        </PAlert>

        <!-- Selected KVK result -->
        <div
          v-else-if="selectedKVKItem && selectedKVKMainAddress"
          class="border-surface gray-lighten-2 relative space-y-2 rounded-md border-2 p-2 text-sm select-none"
        >
          <div class="flex flex-col">
            <span class="font-semibold">Adres</span>
            <span>
              {{ selectedKVKMainAddress.volledigAdres }} <br />
              {{ selectedKVKMainAddress.land }}
            </span>
          </div>
          <div class="flex flex-col">
            <span class="font-semibold">KVK-nummer</span>
            <span>
              {{ selectedKVKItem.kvkNummer }}
            </span>
          </div>
        </div>

        <!-- Existing registered business entity -->
        <div
          v-if="
            knownBusinessEntity.registeredBusinessEntity &&
            !(selectedKVKItem && selectedKVKMainAddress)
          "
          class="border-surface gray-lighten-2 relative space-y-2 rounded-md border-2 p-2 text-sm select-none"
        >
          <div class="flex flex-col">
            <span class="font-semibold">Adres</span>
            <span>
              {{
                knownBusinessEntity.registeredBusinessEntity.getMainBusinessLocation().location
                  .address
              }}
              <br />
              {{ knownBusinessEntity.registeredBusinessEntity.countryOfRegistration }}
            </span>
          </div>
          <div class="flex flex-col">
            <span class="font-semibold">KVK-nummer</span>
            <span>
              {{ knownBusinessEntity.registeredBusinessEntity.registrationNumber }}
            </span>
          </div>
        </div>
      </div>

      <!-- Manual Entry Mode -->
      <div v-show="!useKVKSearch">
        <div class="space-y-2">
          <label class="block">
            <span
              class="after:text-secondary sr-only mb-2 block text-xs font-semibold select-none after:content-['*']"
            >
              Land
            </span>
            <InputCombobox
              v-model="selectedCountryOption"
              :items="countryOptions"
              items-key-name="value"
              items-value-name="label"
              input-placeholder="Selecteer land"
              @item-selected="onCountrySelected"
              :items-filter-fn="filterCountry"
              :max-item-count="50"
              :sort-alphabetically="true"
              grow
              :errors="v$.countryOfRegistration.$errors"
              @focusout="v$.countryOfRegistration.$touch()"
            ></InputCombobox>
          </label>

          <label class="block">
            <span
              class="after:text-secondary sr-only mb-2 block text-xs font-semibold select-none after:content-['*']"
            >
              Bedrijfsnaam
            </span>
            <InputText
              v-model="knownBusinessEntity.customName"
              placeholder="Bedrijfsnaam"
              :errors="v$.customName.$errors"
              @focusout="v$.customName.$touch()"
            />
          </label>

          <label class="block">
            <span
              class="after:text-secondary sr-only mb-2 block text-xs font-semibold select-none after:content-['*']"
            >
              Registratienummer
            </span>
            <InputText
              v-model="knownBusinessEntity.registrationNumber"
              placeholder="Bedrijfsregistratienummer"
              :errors="v$.registrationNumber.$errors"
              @focusout="v$.registrationNumber.$touch()"
            />
          </label>
        </div>
      </div>
    </div>

    <div class="primary-lighten-1 mt-2 w-full space-y-2 rounded-md p-2">
      <label class="block">
        <span class="mb-2 block text-xs font-semibold select-none">Mandagenregister</span>
        <InputSelect
          v-model="selectedManDaysRegisterMode"
          :items="manDaysRegisterModeOptions"
          items-key-name="key"
          items-value-name="label"
          placeholder="Selecteer registratiewijze"
        />
      </label>
    </div>

    <FormValidationAlert v-model="v$" />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, ref, type Ref, useTemplateRef, watch } from "vue"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import InputText from "@/components/inputs/InputText.vue"
import InputSelect from "@/components/inputs/InputSelect.vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import InputSwitch from "@/components/inputs/InputSwitch.vue"
import PAlert from "@/components/elements/PAlert.vue"
import { AlertTypes } from "@/interfaces/component/alert.interface"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import { ManDaysRegisterMode } from "@/enums/man-days-register-mode.enum.ts"
import { KVKRepository } from "@/repositories/external/kvk-repository"
import { countryAndRegistrationNumberIsUniqueValidator } from "@/helper/validation/known-business-entity-validation.ts"
import type { KnownBusinessEntityController } from "@/controllers/model-controllers/known-business-entity-controller.ts"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import countries from "i18n-iso-countries"
import nl from "i18n-iso-countries/langs/nl.json"
import FormValidationAlert from "@/components/validation/FormValidationAlert.vue"
import { requiredValidator } from "@/helper/validation/validators.ts"

const useKVKSearch = ref(true)

const rules = computed(() => {
  const _rules = {
    customName: {},
    countryOfRegistration: {},
    registrationNumber: {},
    $validator: countryAndRegistrationNumberIsUniqueValidator
  }

  if (!useKVKSearch.value) {
    _rules.customName = { requiredValidator }
    _rules.countryOfRegistration = { requiredValidator }
    _rules.registrationNumber = { requiredValidator }
  }

  return _rules
})

const controller: KnownBusinessEntityController = inject("knownBusinessEntityController")!

const {
  model: knownBusinessEntity,
  isCreating,
  initialText,
  v$
} = useFormConfigurator<KnownBusinessEntity>({
  controller,
  rules,
  customSaveHandler: onSave
})
// The user can directly preselect a KVK-result from the KnownBusinessEntityCombobox
// In that case, creationParameters will contain the selected KVKResult.
const creationParameters: { KVKResult: any } = inject("creationParameters")!

const isFormInvalid: Ref<boolean> = inject("isFormInvalid")!

const KVKSearchResults = ref([])
const isSearchingKVK = ref(false)
const lastSearch = ref("")
const inputHasBeenGiven = ref(false)

countries.registerLocale(nl)

const selectedKVKItem = ref<any | null>(null)

const selectedKVKMainAddress = ref<any | null>(null)

// Check whether a company has been selected
const isCompanySelected = computed(() => {
  if (useKVKSearch.value) {
    // KVK mode: just check if we have a selected result
    return !!selectedKVKItem.value
  } else {
    // Manual mode: make sure all required fields are filled
    return !!(
      knownBusinessEntity.value.countryOfRegistration &&
      knownBusinessEntity.value.registrationNumber &&
      knownBusinessEntity.value.customName
    )
  }
})

// Update form invalid state based on company selection and validation
watch(
  [isCompanySelected, () => v$.value.$invalid],
  ([hasCompany, isInvalid]) => {
    if (isCreating.value) {
      // When creating, form is invalid if no company is selected or validation fails
      isFormInvalid.value = !hasCompany || isInvalid
    } else {
      // When editing, only check validation
      isFormInvalid.value = isInvalid
    }
  },
  { immediate: true }
)

const kvkTextOnMounted = computed(() => {
  if (isCreating.value) {
    return initialText ?? ""
  }
  return knownBusinessEntity.value.registeredBusinessEntity?.name
    ? knownBusinessEntity.value.name
    : ""
})

const countryOptions = computed(() => {
  return Object.entries(countries.getNames("nl", { select: "alias" })).map(([code, name]) => ({
    value: code,
    label: name
  }))
})
const selectedCountryOption = ref()
function onCountrySelected(option: any) {
  knownBusinessEntity.value.countryOfRegistration = option?.value
}

// Mandagenregister mode selection
const manDaysRegisterModeOptions = [
  { key: ManDaysRegisterMode.NEVER, label: "Nooit" },
  { key: ManDaysRegisterMode.SEPARATE_ATTACHMENT, label: "Als aparte bijlage" },
  { key: ManDaysRegisterMode.IN_STATEMENT_PDF, label: "In de verzamelstaat (PDF)" }
]

const selectedManDaysRegisterMode = computed({
  get: () =>
    manDaysRegisterModeOptions.find(
      (opt) => opt.key === knownBusinessEntity.value.manDaysRegisterMode
    ) ?? manDaysRegisterModeOptions[0],
  set: (option) => {
    knownBusinessEntity.value.manDaysRegisterMode = option.key
  }
})

// Filter function for country combobox
function filterCountry(item: any, query: string): boolean {
  const queryLower = query.toLowerCase()
  return (
    item.value.toLowerCase().includes(queryLower) || item.label.toLowerCase().includes(queryLower)
  )
}

async function searchKVK(searchTerm: string) {
  if (!searchTerm || searchTerm.trim().length === 0) {
    KVKSearchResults.value = []
    return
  }

  isSearchingKVK.value = true
  const result = await new KVKRepository().searchByName(searchTerm)
  isSearchingKVK.value = false
  if (result.resultaten) {
    KVKSearchResults.value = result.resultaten
    // Ensure combobox opens to show results after search completes
    if (comboboxKVK.value && KVKSearchResults.value.length > 0) {
      comboboxKVK.value.setForceOpenState(true)
    }
  } else {
    KVKSearchResults.value = []
  }
  lastSearch.value = searchTerm
}

let searchTimeout: ReturnType<typeof setTimeout> | null = null
function onKVKQueryChanged(query: string) {
  if (selectedKVKItem.value && query !== selectedKVKItem.value?.naam) {
    // If the user edits the query after selecting an item, treat it as "no selection" again.
    selectedKVKItem.value = null
    selectedKVKMainAddress.value = null
  }
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  searchTimeout = setTimeout(() => {
    inputHasBeenGiven.value = query.length > 0
    searchKVK(query)
  }, 250)
}

const comboboxKVK = useTemplateRef<typeof InputCombobox>("comboboxKVK")
function focusCombobox() {
  comboboxKVK.value?.focus()
}

async function onKVKResultSelected(KVKResult: any) {
  const result = await new KVKRepository().getEstablishmentDetails(KVKResult.vestigingsnummer)
  if (result.adressen) {
    selectedKVKMainAddress.value = result.adressen.find(
      (address: any) => address.type === "bezoekadres"
    )
  }
}

function onKVKItemSelected(item: any) {
  selectedKVKItem.value = item
  onKVKResultSelected(item)
  // Close the dropdown when an item is selected
  if (comboboxKVK.value) {
    comboboxKVK.value.setForceOpenState(false)
    comboboxKVK.value.blur()
  }
}

function onUseKVKSearchChange(value: boolean) {
  if (!value) {
    knownBusinessEntity.value.customName = undefined
  } else {
    // Use the initial text as a starting point for the custom name
    knownBusinessEntity.value.customName = lastSearch.value
  }
}

function switchToManualEntry() {
  useKVKSearch.value = false
  onUseKVKSearchChange(false)
}

async function onSave(save: () => Promise<void>) {
  if (!isCreating.value) {
    // When editing an existing entity we only persist the editable fields
    // (e.g. the man-days register mode) via the standard update flow.
    // Re-creating the entity from its registration number makes no sense here.
    await save()
    return
  }
  if (useKVKSearch.value) {
    // KVK search mode
    const result = await controller.createFromRegistrationNumber(
      "NL",
      selectedKVKItem.value.kvkNummer
    )
    Object.assign(knownBusinessEntity.value, result)
  } else {
    if (
      !knownBusinessEntity.value.countryOfRegistration ||
      !knownBusinessEntity.value.registrationNumber ||
      !knownBusinessEntity.value.customName
    ) {
      throw new Error("All fields are required")
    }
    // Manual input mode
    const result = await controller.createFromRegistrationNumber(
      knownBusinessEntity.value.countryOfRegistration,
      knownBusinessEntity.value.registrationNumber,
      knownBusinessEntity.value.customName
    )
    Object.assign(knownBusinessEntity.value, result)
  }
}

onMounted(() => {
  if (isCreating.value) {
    setTimeout(async () => {
      if (initialText && initialText.length > 0) {
        if (creationParameters?.KVKResult) {
          // In case the KVK result was preselected, use it directly, no need to search
          selectedKVKItem.value = creationParameters.KVKResult
          await onKVKResultSelected(creationParameters.KVKResult)
        } else {
          // Cancel any pending search to avoid duplicates
          if (searchTimeout) {
            clearTimeout(searchTimeout)
            searchTimeout = null
          }
          inputHasBeenGiven.value = true
          // Search immediately when form opens with text
          await searchKVK(initialText)
        }

        // FOCUS HACK: yes, twice
        requestAnimationFrame(() => {
          requestAnimationFrame(async () => {
            focusCombobox()
            // again, twice
            await nextTick()
            await nextTick()

            // Move cursor to the end of the input
            const comboboxElement = comboboxKVK.value?.$el
            const inputElement = comboboxElement?.querySelector("input") as HTMLInputElement
            inputElement?.setSelectionRange(inputElement.value.length, inputElement.value.length)

            // Highlight first option by simulating arrow down key
            inputElement?.dispatchEvent(
              new KeyboardEvent("keydown", { key: "ArrowDown", bubbles: true })
            )
            inputElement?.dispatchEvent(
              new KeyboardEvent("keyup", { key: "ArrowDown", bubbles: true })
            )
          })
        })
      }
    }, 0)
  } else {
    if (knownBusinessEntity.value.registeredBusinessEntity?.name) {
      setTimeout(() => {
        focusCombobox()
        selectedKVKMainAddress.value = knownBusinessEntity.value.registeredBusinessEntity
      }, 0)
    } else {
      useKVKSearch.value = false
      // Set the country dropdown to the existing value if we have one
      if (knownBusinessEntity.value.countryOfRegistration) {
        selectedCountryOption.value = countryOptions.value.find(
          (opt) => opt.value === knownBusinessEntity.value.countryOfRegistration
        )
      }
    }
  }
})
</script>

<style scoped></style>
